
import { defineComponent, onMounted, ref, watch } from 'vue';

import type { PropType } from 'vue';
import type { PluginPackageInitializeModuleOptions } from '@/types';

interface InitializeResult {
    status: 'success' | 'error' | 'pending' | 'notified';
    error?: unknown;
}

export default defineComponent({
    name: 'mhtk-module-injector',
    emits: [
        'initialize-success',
        'initialize-error'
    ],
    props: {
        initializeModule: {
            type: Function,
            required: true
        },
        initializeModuleOptions: {
            type: Object as PropType<PluginPackageInitializeModuleOptions>,
            required: true
        }
    },
    setup(props, { emit }) {
        let isMounted = false;

        const initializeResult = ref<InitializeResult>({
            status: 'pending'
        });

        function notifyInitializationStatus() {
            if (initializeResult.value.status === 'success') {
                emit('initialize-success');
                initializeResult.value.status = 'notified';
            } else if (initializeResult.value.status === 'error') {
                emit('initialize-error', initializeResult.value.error);
                initializeResult.value.status = 'notified';
            }
        }

        watch(() => initializeResult.value, () => {
            if (isMounted) {
                notifyInitializationStatus();
            }
        });

        // Need to be careful about async here, injection context is lost the second you use an async function.
        try {
            const result = props.initializeModule(props.initializeModuleOptions);
            if (result?.then) {
                result.then(() => {
                    initializeResult.value = {
                        status: 'success'
                    };
                }).catch((error: unknown) => {
                    initializeResult.value = {
                        status: 'error',
                        error
                    };
                });
            } else {
                initializeResult.value = {
                    status: 'success'
                };
            }
        } catch (error) {
            initializeResult.value = {
                status: 'error',
                error
            };
        }

        onMounted(() => {
            isMounted = true;
            notifyInitializationStatus();
        });
    }
});
